import { busy, notBusy } from '../busy/actions.js'
import { handleAddErrors, handleGetErrors, handleUpdateErrors, handleDeleteErrors } from '../error/actions.js'
import { resetFileSearch } from '../files/actions.js'

import api from '../api.js'
import store from '../store.js'
import storage from '../storage.js'

export const CATEGORY_CREATE_ACTION = 'CATEGORY_CREATE_ACTION'
export const CATEGORY_SHOW_ADD_DIALOG_ACTION = 'CATEGORY_SHOW_ADD_DIALOG_ACTION'
export const CATEGORY_HIDE_ADD_DIALOG_ACTION = 'CATEGORY_HIDE_ADD_DIALOG_ACTION'
export const CATEGORY_GET_OK_ACTION = 'CATEGORY_GET_OK_ACTION'
export const CATEGORY_UPDATE_ACTION = 'CATEGORY_UPDATE_ACTION'
export const CATEGORY_SHOW_EDIT_DIALOG_ACTION = 'CATEGORY_SHOW_EDIT_DIALOG_ACTION'
export const CATEGORY_HIDE_EDIT_DIALOG_ACTION = 'CATEGORY_HIDE_EDIT_DIALOG_ACTION'
export const CATEGORY_DELETE_ACTION = 'CATEGORY_DELETE_ACTION'
export const CATEGORY_SHOW_DELETE_DIALOG_ACTION = 'CATEGORY_SHOW_DELETE_DIALOG_ACTION'
export const CATEGORY_HIDE_DELETE_DIALOG_ACTION = 'CATEGORY_HIDE_DELETE_DIALOG_ACTION'

export const CATEGORY_GET_PAGE_ITEMS_ACTION = 'CATEGORY_GET_PAGE_ITEMS_ACTION'
export const CATEGORY_SET_ORDER_ACTION = 'CATEGORY_SET_ORDER_ACTION'
export const CATEGORY_SET_LIMIT_ACTION = 'CATEGORY_SET_LIMIT_ACTION'
export const CATEGORY_SET_OFFSET_ACTION = 'CATEGORY_SET_OFFSET_ACTION'
export const CATEGORY_SET_SEARCH_ACTION = 'CATEGORY_SET_SEARCH_ACTION'
export const CATEGORY_RESET_SEARCH_ACTION = 'CATEGORY_RESET_SEARCH_ACTION'
export const CATEGORY_SET_FILTER_QUERIES_ACTION = 'CATEGORY_SET_FILTER_QUERIES_ACTION'
export const CATEGORY_SET_SELECTED_ITEM_ID = 'CATEGORY_SET_SELECTED_ITEM_ID'

const TAG_FILTER_KEY = 'tags_string'
const OBJECT_TYPE_FILTER_KEY = 'object_type_name_string'
const TAG_FILTER_OP = '~'
const TAG_FILTER_MODE_OR = 'or'
const TAG_FILTER_MODE_AND = 'and'

function filterItemsByTagNames (items, selectedTagNames, matchMode = TAG_FILTER_MODE_OR) {
  if (!Array.isArray(selectedTagNames) || selectedTagNames.length === 0) {
    return items
  }

  const selected = new Set(
    selectedTagNames
      .filter((value) => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim().toLowerCase())
  )

  if (selected.size === 0) {
    return items
  }

  return items.filter((item) => {
    const tagNames = (item?.tags || [])
      .map((tag) => tag?.name)
      .filter((name) => typeof name === 'string' && name.trim().length > 0)
      .map((name) => name.trim().toLowerCase())

    if (matchMode === TAG_FILTER_MODE_AND) {
      return Array.from(selected).every((selectedTag) => tagNames.includes(selectedTag))
    }
    return tagNames.some((name) => selected.has(name))
  })
}

function filterItemsByAnyValues (items, selectedValues, getValue) {
  if (!Array.isArray(selectedValues) || selectedValues.length === 0) {
    return items
  }

  const selected = new Set(
    selectedValues
      .filter((value) => typeof value === 'string' && value.trim().length > 0)
      .map((value) => value.trim().toLowerCase())
  )

  if (selected.size === 0) {
    return items
  }

  return items.filter((item) => {
    const value = getValue(item)
    return typeof value === 'string' && selected.has(value.trim().toLowerCase())
  })
}

export function categoryCreateAction (category) {
  return {
    type: CATEGORY_CREATE_ACTION,
    category
  }
}

export function categoryUpdateAction (category) {
  return {
    type: CATEGORY_UPDATE_ACTION,
    category
  }
}

export function categoryDeleteAction (category) {
  return {
    type: CATEGORY_DELETE_ACTION,
    category
  }
}

export function setSelectedItemId (selectedItemId) {
  return function (dispatch) {
    dispatch({
      type: CATEGORY_SET_SELECTED_ITEM_ID,
      selectedItemId
    })
    dispatch(resetFileSearch('category'))
  }
}

/**
 * Add dialog
 */
export function showAddCategoryDialog () {
  return {
    type: CATEGORY_SHOW_ADD_DIALOG_ACTION
  }
}

export function hideAddCategoryDialog () {
  return {
    type: CATEGORY_HIDE_ADD_DIALOG_ACTION
  }
}

/**
 * Edit dialog
 */
export function showEditCategoryDialog (category) {
  return {
    type: CATEGORY_SHOW_EDIT_DIALOG_ACTION,
    category
  }
}

export function hideEditCategoryDialog () {
  return {
    type: CATEGORY_HIDE_EDIT_DIALOG_ACTION
  }
}

/**
 * Delete dialog
 */
export function showDeleteCategoryDialog (category) {
  return {
    type: CATEGORY_SHOW_DELETE_DIALOG_ACTION,
    category
  }
}

export function hideDeleteCategoryDialog () {
  return {
    type: CATEGORY_HIDE_DELETE_DIALOG_ACTION
  }
}

/**
 * CREATE
 */
export function addCategory (category) {
  return function (dispatch) {
    dispatch(busy())
    api.addCategory(category).then(() => {
      dispatch(notBusy())
    }).catch(handleAddErrors('category', dispatch))
  }
}

/**
 * READ
 */
export function getCategories (params = {}) {
  return function (dispatch) {
    dispatch(busy())
    api.getCategories(params).then(result => {
      dispatch(notBusy())
      dispatch(receivedCategories(result.data.value))
    }).catch(handleGetErrors('category', dispatch))
  }
}

export function receivedCategories (items) {
  return {
    type: CATEGORY_GET_OK_ACTION,
    items
  }
}

/**
 * UPDATE
 */
export function updateCategory (category) {
  return function (dispatch) {
    dispatch(busy())
    api.updateCategory(category).then(() => {
      dispatch(notBusy())
    }).catch(handleUpdateErrors('category', dispatch))
  }
}

/**
 * DELETE
 */
export function deleteCategory (category) {
  return function (dispatch) {
    dispatch(busy())
    api.deleteCategory(category).then(() => {
      dispatch(notBusy())
    }).catch(handleDeleteErrors('category', dispatch))
  }
}

export function toggleFavorite (category) {
  return function (dispatch) {
    const favorite = !category.is_favorite
    dispatch(busy())
    api.setCategoryFavorite(category.id, favorite).then(() => {
      dispatch(notBusy())
    }).catch(handleUpdateErrors('category', dispatch))
  }
}

/**
 * Set filter queries
 */
export function setFilterQueries (filterQueries) {
  storage.putPageSetting('category', { filterQueries })
  return {
    type: CATEGORY_SET_FILTER_QUERIES_ACTION,
    filterQueries
  }
}
/**
 * Get items for a page
 */
export function getPageItems () {
  const state = store.getState()
  const { search, limit, offset, orderBy, order, filterQueries } = state.category

  let query = []
  if (typeof search === 'string' && search.length > 0) {
    const split = search.split(',').map(s => s.trim())
    if (split.length === 1) {
      if (split[0].length > 0) query.push({ key: 'name', value: split[0], op: '~' })
    } else if (split.length === 2) {
      if (split[0].length > 0) query.push({ key: 'name', value: split[0], op: '~' })
      if (split[1].length > 0) query.push({ key: 'collection_name', value: split[1], op: '~' })
    }
  }

  const allFilters = Array.isArray(filterQueries) ? filterQueries : []
  const tagFilters = allFilters.filter((q) => q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP)
  const objectTypeFilters = allFilters.filter((q) => q.key === OBJECT_TYPE_FILTER_KEY && q.op === TAG_FILTER_OP)
  const nonTagFilters = allFilters.filter((q) => {
    const isTagFilter = q.key === TAG_FILTER_KEY && q.op === TAG_FILTER_OP
    const isObjectTypeFilter = q.key === OBJECT_TYPE_FILTER_KEY && q.op === TAG_FILTER_OP
    return !isTagFilter && !isObjectTypeFilter
  })
  const selectedTagNames = tagFilters.map((q) => q.value)
  const selectedObjectTypeNames = objectTypeFilters.map((q) => q.value)
  const selectedTagMatchMode = tagFilters.find((q) => q.matchMode === TAG_FILTER_MODE_AND)
    ? TAG_FILTER_MODE_AND
    : TAG_FILTER_MODE_OR
  query = [...query, ...nonTagFilters]

  const sort = orderBy ? `${orderBy}.${order}` : null

  const numericLimit = Number(limit)
  const pageLimit = Number.isFinite(numericLimit) && numericLimit > 0 ? numericLimit : 50
  const numericOffset = Number(offset) || 0

  if (selectedTagNames.length > 0 || selectedObjectTypeNames.length > 0) {
    return function (dispatch) {
      dispatch(busy())
      api.getCategories({
        query,
        sort,
        limit: Math.max(1000, numericOffset + pageLimit),
        offset: 0
      }).then(result => {
        let filteredItems = result.data.value
        filteredItems = filterItemsByTagNames(filteredItems, selectedTagNames, selectedTagMatchMode)
        filteredItems = filterItemsByAnyValues(filteredItems, selectedObjectTypeNames, (item) => item?.object_type_name)
        const pageItems = filteredItems.slice(numericOffset, numericOffset + pageLimit)
        dispatch(notBusy())
        dispatch(receivedCategories(pageItems))
      }).catch(handleGetErrors('category', dispatch))
    }
  }

  return function (dispatch) {
    getCategories({
      query,
      limit,
      offset,
      sort
    })(dispatch)
  }
}

/**
 * Sort order
 */
export function setOrder (orderBy, order) {
  return {
    type: CATEGORY_SET_ORDER_ACTION,
    orderBy,
    order
  }
}

/**
 * Set limit for pagination
 */
export function setLimit (limit) {
  return {
    type: CATEGORY_SET_LIMIT_ACTION,
    limit
  }
}

/**
 * Set offset for pagination
 */
export function setOffset (offset) {
  return {
    type: CATEGORY_SET_OFFSET_ACTION,
    offset
  }
}

/**
 * Set search
 */
export function setSearch (search) {
  return {
    type: CATEGORY_SET_SEARCH_ACTION,
    search
  }
}

/**
 * Reset search
 */
export function resetSearch () {
  return {
    type: CATEGORY_RESET_SEARCH_ACTION
  }
}
